/**
 * **The matter's estate record, as attribute specs** (VALE-90, reshaped by
 * VALE-130).
 *
 * A subset of the field set the **Manage Estate Details** drawer
 * (`edit-matter-dialog.tsx`) manages, expressed as rows of the shared attribute
 * grid so the record can be read and edited in place on the matter Overview.
 * The drawer is untouched and stays the place the **full** record is managed.
 *
 * ## This module is the matter's field vocabulary, and that is why it is here
 *
 * `components/leads/lead-field-specs.ts` is the *enquiry's* vocabulary — every
 * spec in it patches the enquiry. {@link actingSolicitorField} patches the
 * **matter**, and lived in `lead-matter-panel.tsx` only because that was its
 * single consumer; its own doc said as much. It now has a second consumer (the
 * Overview card), so it moved here rather than being copied, and the enquiry
 * band imports it from here. One definition, two surfaces — the root
 * `CLAUDE.md` *bind to the source of truth* rule.
 *
 * ## Every rule that is not obvious from the row is written where the row is
 *
 * Three commit contracts here are invisible from the drawer, and each one fails
 * **silently or with a 400** rather than visibly:
 *
 * 1. **`roles` is a full sync, not a merge** — see {@link actingSolicitorField}
 *    and {@link matterTeamField}, which are two controls over one array on two
 *    different surfaces, and each has to carry forward every assignment it does
 *    not itself own. (VALE-166 closed the worst case of this: the card's own two
 *    controls became one row, so neither can drop the other's half any more.)
 * 2. **A cleared date must send `null`, never `''`** — see {@link decedentDateField}.
 * 3. **A cleared address must send empty parts, never `null`** — see {@link decedentAddressField}.
 *
 * Every one of them is enforced here, in `toPatch` / `validate`, rather than at
 * the call site: a rule a call site has to remember is a rule the *next* call
 * site gets wrong, which is §2 of `components/attributes/CLAUDE.md` one level
 * down.
 *
 * ## What VALE-130 removed, and why it is not a regression
 *
 * **The estate bank account's three rows are gone.** VALE-91 reviewed the card
 * in the browser and found the account name, BSB and account number rendering
 * in plain text on the matter landing tab, on every visit, for anyone who can
 * see the matter — an identifier set that has no reason to be the first thing
 * on the page. The drawer keeps all three and stays where the account is
 * managed; the estate-account page under Distribution is about cash and rounds,
 * not the account identifiers.
 *
 * Deleting the triple also deletes the only whole-object cross-field
 * validation on this surface, and with it the refusal VALE-91 caught: typing a
 * BSB alone produced five sentences, *"not saved"* twice, two contradictory
 * rules fired on an empty field the reader never touched, and a closing
 * instruction to go and use the drawer instead. Nothing on the card now
 * validates one field against another, so no refusal can name a field the
 * reader was not editing.
 */

import {
  bankAccountCopyText,
  formatBankAccountSummary
} from '@/components/attributes';
import type {
  AttributeBankAccount,
  AttributeDraft,
  AttributeNameParts,
  AttributePrincipalMembers,
  AttributeRowsBlock,
  AttributeSpec
} from '@/components/attributes';
import { DECEASED_TITLE_OPTIONS } from '@/lib/constants';
import { formatAddress } from '@/lib/formatters/contact-formatters';
import { formatSpelledDate } from '@/components/leads/lead-row-format';
import type { LawFirmUser } from '@/lib/services/account/types';
import {
  UpdateMatterRequestSchema,
  type Matter,
  type MatterRoleAssignment,
  type PostalAddress,
  type UpdateMatterRequest
} from '@/lib/services/matters/types';
import { formatDecedentName } from '@/lib/services/matters/utils';
import {
  NO_OFFICE,
  resolveMatterOfficeOptions
} from '@/components/matters/matter-office-options';
import { getUserDisplayName } from '@/lib/utils/permissions';
import { resolveDemoMatterFlag } from '@/lib/utils/demo-matter';

/**
 * One section of the card — a `rows` block of `AttributeSections`' own
 * composition vocabulary (LF-6350 / VALE-73).
 *
 * Emitted directly rather than as a private `{ title, fields }` shape the card
 * then maps: the block type *is* the shared component's input, so a local
 * intermediate would be a second spelling of it with nothing to gain.
 */
export type MatterAttributeBlock = AttributeRowsBlock<UpdateMatterRequest>;

const asText = (draft: unknown): string =>
  typeof draft === 'string' ? draft : '';

/** `''` → `null`: an emptied field means *clear the column*. */
const blankToNull = (value: string): string | null =>
  value.length === 0 ? null : value;

/**
 * Every role assignment on the matter **except** the one a given control owns,
 * as the patch shape `roles` takes.
 *
 * The shared half of contract 1 above: `MattersService.update` hands the array
 * straight to `rolesService.syncRoles`, which makes the payload the matter's
 * complete role list. Two controls on this card write that one array — the
 * acting solicitor and the assisting members — so each has to carry the other's
 * assignments forward or it silently unassigns them. Written once here rather
 * than twice, because the second copy is the one that gets it wrong.
 */
function rolesOtherThan(
  matter: Matter | undefined,
  owned: MatterRoleAssignment['role'] | MatterRoleAssignment['role'][]
): { userId: number; role: MatterRoleAssignment['role'] }[] {
  const ownedRoles = Array.isArray(owned) ? owned : [owned];
  return (matter?.roles ?? [])
    .filter((assignment) => !ownedRoles.includes(assignment.role))
    .map((assignment) => ({
      userId: assignment.user.id,
      role: assignment.role
    }));
}

// =============================================================================
// Matter
// =============================================================================

/**
 * The firm's own reference for the matter.
 *
 * **It carries the demo flag down with it, and that is not a decoration**
 * (VALE-892). `Matter.isDemo` may only be `true` while the reference announces
 * the matter as a demo — the backend re-checks it on every write
 * (`assertDemoFlagAllowed`), and `DemoMatterCheckbox` stops rendering the moment
 * the reference stops qualifying. The whole-form submit this row replaced always
 * sent both together through {@link resolveDemoMatterFlag}, so renaming
 * `DEMO-004` to `M2024-001` turned the flag off in the same request.
 *
 * A per-field commit sends only the row that changed, so without this the flag
 * would survive its own justification: a matter that is silently never billed,
 * never synced to the firm's PMS and has every outbound email redirected, with
 * nothing in its reference left to say so. The backend would not refuse it —
 * its guard only fires on `isDemo: true` — which is exactly why the clearing
 * has to be expressed here.
 *
 * Only ever *clears*. Setting the flag stays a deliberate tick of the checkbox.
 */
function matterReferenceField(
  matter: Matter | undefined
): AttributeSpec<UpdateMatterRequest> {
  const value = matter?.lawFirmInternalReference ?? '';
  return {
    label: 'Matter reference',
    display: value || null,
    draft: value,
    editor: { kind: 'text' },
    toPatch: (draft) => {
      const lawFirmInternalReference = asText(draft);
      const stillADemo = resolveDemoMatterFlag(
        lawFirmInternalReference,
        matter?.isDemo
      );
      return matter?.isDemo && !stillADemo
        ? { lawFirmInternalReference, isDemo: false }
        : { lawFirmInternalReference };
    }
  };
}

/**
 * **The client — the legal personal representative, read-only** (VALE-130
 * follow-up).
 *
 * The person the firm is actually acting for, and the row a coordinator opening
 * a matter reaches for most often after the deceased's name. It is
 * `readOnly` because there is nothing here to patch: an LPR is a **contact
 * relationship** (`legalPersonalRepresentatives`, each carrying its own
 * `legalPersonalRepresentativeRole`), so appointing or replacing one is contact
 * work and lives on the Contacts tab. A row that opened an editor with no
 * column behind it would be worse than no row; a row that shows the answer and
 * points nowhere is exactly right.
 *
 * **`editor` is still required by the type and is never reached.** `text` is
 * the inert choice — `readOnly` makes the read-out a span, so no branch of
 * `attribute-field-control.tsx` ever mounts. It is what makes the copy
 * affordance derive, which is wanted: an executor's name gets pasted into
 * letters and court forms all day.
 *
 * **All of them, comma-joined, not the first.** An estate with two executors
 * has two clients, and naming one of them is a wrong answer rather than a short
 * one.
 */
function clientField(
  matter: Matter | undefined
): AttributeSpec<UpdateMatterRequest> {
  const names = (matter?.legalPersonalRepresentatives ?? [])
    .filter((lpr) => lpr.isLegalPersonalRepresentative !== false)
    .map((lpr) =>
      [lpr.firstName, lpr.middleName, lpr.lastName]
        .filter((part) => (part ?? '').trim().length > 0)
        .join(' ')
    )
    .filter((name) => name.length > 0);
  const formatted = names.join(', ');

  return {
    label: 'Client',
    display: formatted || null,
    draft: formatted,
    editor: { kind: 'text' },
    readOnly: true,
    copyText: formatted,
    toPatch: () => null
  };
}

// =============================================================================
// Deceased
// =============================================================================

/**
 * The two labels the drawer's identity block is anchored on (VALE-892).
 *
 * `Other names` sits beside `Deceased` and `Other addresses` beside
 * `Residential address`, and the splice that places them matches on the label
 * string — so a literal at the call site is a second definition that goes stale
 * silently the day either row is renamed. Exported for the same reason
 * `contact-attribute-specs.ts` exports its own two.
 */
export const DECEASED_NAME_LABEL = 'Deceased';
export const RESIDENTIAL_ADDRESS_LABEL = 'Residential address';

/**
 * The deceased's title, first, middle and last name as **one** row (VALE-130).
 *
 * **Four rows was the shape, and it was wrong for two reasons.** VALE-91 read
 * them as four unrelated fields rather than as one person, and the sidebar
 * already prints `ESTATE OF THE LATE / <name>` about 200px to the left — so the
 * card spent four of its thirteen rows restating, badly, the one thing the page
 * was already loudest about. `name` (VALE-129) is the editor kind that makes
 * one row possible: a popover holding all four parts, on the same
 * Escape-reverts / dismiss-commits contract the address row uses.
 *
 * **The honorific is bound to {@link DECEASED_TITLE_OPTIONS}, and the drawer's
 * own list is deliberately left alone.** The drawer hand-types a six-member
 * subset (it omits `Rev` and `Hon`) and adds a seventh entry, `None`, whose
 * value is the literal string `'none'` — so choosing it there stores `"none"`
 * as somebody's title and renders *"your late none"*. Binding to the constant
 * fixes both halves here; the drawer is out of this ticket's scope.
 *
 * **Clears to `null`, and commits four empty strings.** `null` is the draft
 * convention every popover kind in this grid shares (see
 * `attribute-name-editor.tsx`'s `normalise` — four empty strings would still
 * read as *something is captured here*). The patch cannot carry that `null`
 * through: all four columns are `z.string().optional()` and none is
 * `.nullable()`, so `''` is what clears them, which is also exactly what the
 * drawer sends when its inputs are emptied.
 *
 * **It carries the date of death as a badge** (VALE-166). The date is read far
 * more often than it is edited — every downstream form wants it — but a
 * labelled summary row of its own bought nothing that `Jane Doe · 12 March
 * 2024` does not say more briefly, and the summary set is on a fold budget. So
 * the date moves behind *See more* as an editable row and reappears here as an
 * ornament. **Spelled through {@link formatSpelledDate}, the same helper
 * {@link decedentDateField} reads out with** — the two must not drift into two
 * spellings of one date on one card, which is the root `CLAUDE.md` *bind to the
 * source of truth* rule at its smallest scale. No date recorded renders no
 * badge, per {@link AttributeSpec.badge}.
 */
function deceasedNameField(
  matter: Matter | undefined,
  /*
    **The badge earns its place only where the date's own row is hidden**
    (VALE-892). On the card `Date of death` sits behind *See more*, so the badge
    is the only read of it — that is the whole argument for putting it here. The
    drawer shows every row at once, so the same badge renders `1 January 2024`
    twice on screen, once unlabelled: the ornament and the field it stands in
    for, three rows apart. So the drawer asks for it off.
  */
  { withDateOfDeathBadge = true }: { withDateOfDeathBadge?: boolean } = {}
): AttributeSpec<UpdateMatterRequest> {
  const parts: AttributeNameParts = {
    title: matter?.decedentTitle ?? '',
    firstName: matter?.decedentFirstName ?? '',
    middleName: matter?.decedentMiddleName ?? '',
    lastName: matter?.decedentLastName ?? ''
  };
  const recorded = Object.values(parts).some(
    (part) => (part ?? '').trim().length > 0
  );
  /*
    `formatDecedentName` owns how a decedent's name reads across the product, so
    the row binds to it rather than re-joining the parts — but only once we know
    at least one is set, because its no-name fallback is the literal string
    `Unnamed`, and this grid's empty state is the grey `Set deceased…`
    placeholder the read-out derives. The `Unnamed` branch is therefore
    unreachable from here by construction, not by luck.
  */
  const formatted = recorded ? formatDecedentName(matter ?? {}) : '';
  const dateOfDeath = matter?.decedentDateOfDeath ?? '';

  return {
    label: DECEASED_NAME_LABEL,
    display: formatted || null,
    badge:
      withDateOfDeathBadge && dateOfDeath
        ? formatSpelledDate(`${dateOfDeath}T00:00:00`)
        : null,
    draft: recorded ? parts : null,
    editor: { kind: 'name', titleOptions: DECEASED_TITLE_OPTIONS },
    /*
      A `name` derives no copy affordance (`offersCopy` covers `text`), and
      folding four `text` rows into one would otherwise have taken the copy
      button away from a value people paste into court forms. Same pairing the
      address row uses, for the same reason.
    */
    copyable: true,
    copyText: formatted,
    clearTo: null,
    toPatch: (draft) => {
      if (typeof draft === 'string') return null;
      const next = draft as AttributeNameParts | null;
      return {
        decedentTitle: next?.title ?? '',
        decedentFirstName: next?.firstName ?? '',
        decedentMiddleName: next?.middleName ?? '',
        decedentLastName: next?.lastName ?? ''
      };
    }
  };
}

/**
 * A decedent-related date, read out spelled — the two decedent dates plus the
 * date the deceased's will was signed (`willDate`).
 *
 * **Clears to `null`, and that is load-bearing rather than tidy.** The backend
 * validates all three date columns with `@Matches(/^\d{4}-\d{2}-\d{2}$/)`
 * alongside `@IsOptional()`, and `@IsOptional()` skips its validators for
 * `null` and `undefined` **only** — an empty string is a value, so it reaches
 * `@Matches` and fails it. A `date` row derives the clear `×` (`offersClear`),
 * so the default `clearTo` of `''` would put an affordance on screen whose
 * only possible outcome is a 400. `null` is the one draft that both passes
 * validation and actually nulls the column.
 *
 * That is also why `UpdateMatterRequestSchema` carries `.nullable()` on these
 * three fields: the backend has always accepted `null` here, and the client
 * schema simply could not say so, which made clearing unexpressible from any
 * surface rather than merely unimplemented on this one.
 */
function decedentDateField(
  label: string,
  key: 'decedentDateOfBirth' | 'decedentDateOfDeath' | 'willDate',
  matter: Matter | undefined
): AttributeSpec<UpdateMatterRequest> {
  const value = matter?.[key] ?? '';
  return {
    label,
    display: value ? formatSpelledDate(`${value}T00:00:00`) : null,
    draft: value,
    editor: { kind: 'date' },
    clearTo: null,
    toPatch: (draft) => {
      if (draft === null) return { [key]: null };
      return { [key]: blankToNull(asText(draft)) };
    }
  };
}

/**
 * The death-certificate registration number.
 *
 * `validate` binds to the schema's own rule rather than restating `64`: the
 * bound is declared once, on `UpdateMatterRequestSchema`, and a second copy
 * here would be correct only until somebody moves it.
 *
 * **It is now the card's only `validate`** (VALE-130), which makes it the one
 * row that can exercise §6's refusal path — a reason the reader can act on,
 * attached to the row that failed, with the typing preserved. Unlike the estate
 * bank account's whole-object rule that it outlived, it reads exactly one
 * field, so a refusal can only ever be about the field being edited.
 */
function deathCertificateField(
  matter: Matter | undefined
): AttributeSpec<UpdateMatterRequest> {
  const value = matter?.deathCertificateRegistrationNumber ?? '';
  return {
    label: 'Death certificate number',
    display: value || null,
    draft: value,
    editor: { kind: 'text' },
    validate: (draft) => {
      const parsed =
        UpdateMatterRequestSchema.shape.deathCertificateRegistrationNumber.safeParse(
          blankToNull(asText(draft))
        );
      return parsed.success
        ? null
        : (parsed.error.issues[0]?.message ??
            'That death certificate number was not saved.');
    },
    // The drawer's own submit-time conversion: `''` becomes `null` so the
    // column is cleared rather than filled with an empty string.
    toPatch: (draft) => ({
      deathCertificateRegistrationNumber: blankToNull(asText(draft))
    })
  };
}

/**
 * The two identity facts a court-application precedent recites that Vale did not
 * hold until VALE-809 — the deceased's occupation and where they died.
 *
 * One factory for both, because they are the same row shape over two columns and
 * two copies of it would drift the moment either bound changed. `validate` binds
 * to the schema's own rule rather than restating `255`, exactly as the death
 * certificate number above does.
 *
 * **Free text, deliberately.** Both are reproduced from the death certificate,
 * and an affidavit swears to the description the certificate carries — so a
 * picker or a normalising transform would put a word in the deponent's mouth
 * that the certificate does not use.
 */
function decedentTextField(
  label: string,
  key: 'decedentOccupation' | 'decedentPlaceOfDeath',
  matter: Matter | undefined
): AttributeSpec<UpdateMatterRequest> {
  const value = matter?.[key] ?? '';
  return {
    label,
    display: value || null,
    draft: value,
    editor: { kind: 'text' },
    validate: (draft) => {
      const parsed = UpdateMatterRequestSchema.shape[key].safeParse(
        blankToNull(asText(draft))
      );
      return parsed.success
        ? null
        : (parsed.error.issues[0]?.message ??
            `That ${label.toLowerCase()} was not saved.`);
    },
    // `''` becomes `null` so the column is cleared rather than filled with an
    // empty string — which the merge fields would resolve as present-but-blank.
    toPatch: (draft) => ({ [key]: blankToNull(asText(draft)) })
  };
}

/**
 * **Where the original will is held** (VALE-1402). Free text, carried forward
 * from the enquiry at conversion and correctable here afterwards.
 *
 * Its own factory rather than a third key on {@link decedentTextField}: that one
 * is documented as the two facts *the death certificate recites*, and widening
 * it would make its doc false. Same row shape, one fact.
 *
 * **`multiline`, unlike its neighbours.** Every other row in this group holds a
 * value: a date, a registration number, an occupation. This one holds a
 * sentence ("With John Smith Solicitors in Bondi"), and at a value's width it
 * ellipsises to exactly the half that identifies nothing. `Other parties` on the
 * enquiry's own Estate card took the same pair for the same reason.
 *
 * **The placeholder is the intake form's own**, verbatim, so a solicitor reading
 * the matter and a client filling the intake form are shown the same example of
 * what belongs here.
 *
 * **Nothing writes this but the conversion and this row.** A client's own
 * `aboutWill.willStorageLocation` intake answer stays on the Intake form tab and
 * in the instruction sheet; binding it here would let a later submission
 * silently overwrite a correction a solicitor made after reading it, and no
 * intake answer writes a matter column today.
 */
function willStorageLocationField(
  matter: Matter | undefined
): AttributeSpec<UpdateMatterRequest> {
  const value = matter?.willStorageLocation ?? '';
  return {
    label: 'Stored at',
    display: value || null,
    draft: value,
    editor: {
      kind: 'text',
      placeholder:
        'e.g. With John Smith Solicitors in Bondi, or in a safe at home'
    },
    fullWidth: true,
    multiline: true,
    validate: (draft) => {
      const parsed =
        UpdateMatterRequestSchema.shape.willStorageLocation.safeParse(
          blankToNull(asText(draft))
        );
      return parsed.success
        ? null
        : (parsed.error.issues[0]?.message ??
            'That will location was not saved.');
    },
    // `''` becomes `null` so the column is cleared rather than holding a blank.
    toPatch: (draft) => ({ willStorageLocation: blankToNull(asText(draft)) })
  };
}

/**
 * The deceased's residential address.
 *
 * **Clears to five empty parts, not to `null`.** The attribute grid's own
 * address convention is `clearTo: null` (`attribute-address-editor`'s
 * `normalise` — five empty strings would still read as *something is captured
 * here*), and this row keeps that draft convention. What it must **not** do is
 * pass that `null` through to the patch: `decedentAddress` is
 * `PostalAddressSchema.optional()` and **not** `.nullable()`, so
 * `{ decedentAddress: null }` fails client-side in `updateMatterAction` before
 * any HTTP call — a "Validation failed" on the one gesture that should be the
 * simplest. Empty parts are what the drawer sends when its inputs are emptied,
 * so this is parity rather than a new shape.
 */
function decedentAddressField(
  matter: Matter | undefined
): AttributeSpec<UpdateMatterRequest> {
  const address = matter?.decedentAddress;
  const formatted = address ? formatAddress(address) : '';
  const draft: PostalAddress | null = address
    ? {
        streetAddress: address.streetAddress,
        suburb: address.suburb,
        state: address.state,
        postcode: address.postcode,
        country: address.country
      }
    : null;

  return {
    label: RESIDENTIAL_ADDRESS_LABEL,
    display: formatted || null,
    draft,
    editor: { kind: 'address' },
    copyable: true,
    copyText: formatted,
    /*
      **No `fullWidth`, and it still wraps.** The row spent a whole 1100px band
      of the card on ~350px of address, and — because the read-out's target was
      the cell — that band was one hover target: three-quarters of it empty
      space that lit up as though it were the value. `fullWidth` is a *span*, and
      the reason this row wanted one was never the span; it was that an address
      truncated to `12 Example St, Syd…` is useless. That is now derived from
      `editor.kind === 'address'` in `attribute-read-out.tsx`, so the address
      wraps to two lines inside its own third of the grid and the row is a row.
    */
    clearTo: null,
    toPatch: (nextDraft) => {
      if (typeof nextDraft === 'string') return null;
      const next = nextDraft as PostalAddress | null;
      return {
        decedentAddress: {
          streetAddress: next?.streetAddress ?? '',
          suburb: next?.suburb ?? '',
          state: next?.state ?? '',
          postcode: next?.postcode ?? '',
          country: next?.country ?? ''
        }
      };
    }
  };
}

/**
 * **The deceased, as the AI-extract review draws them** (VALE-1183).
 *
 * A third consumer of the same four builders the card and the Manage Estate
 * Details drawer render — so a rule about how a decedent date clears, or that a
 * cleared address must send five empty parts rather than `null`, is inherited
 * here rather than restated for a surface that commits into a pending extract.
 * That is the same argument {@link buildMatterEstateAttributeSpecs} and
 * {@link buildMatterDrawerAttributeBlocks} make for each other, and the same one
 * VALE-1196 made when the compact contacts block moved onto the contact form's
 * own specs: the review block used to restate seven fields as read-only text,
 * which is how it came to have a curated set that could silently drop a changed
 * field.
 *
 * ## Four rows, and the omissions are deliberate
 *
 * The drawer's deceased block carries four more — the will date, the death
 * certificate number, the occupation and the place of death. **A review surface
 * must not offer them**, and not because they are unimportant: neither review
 * payload has a column for them. `PreviewDeceased` is the wire's whole
 * vocabulary for the deceased and holds none of the four, so a row here would
 * render empty on every extract, and an edit typed into it would commit to a
 * draft that gets dropped on the way to apply. A row that silently loses what
 * is typed into it is worse than no row.
 *
 * **No date-of-death badge.** The badge exists for a surface where the date's
 * own row is hidden behind *See more*; this grid shows both rows at once, so it
 * would render the same date twice, once unlabelled — the drawer's own reason
 * for asking it off.
 *
 * A pure function of the deceased, like both builders above, so every rule
 * stays a unit-testable `toPatch`.
 */
export function buildDeceasedReviewAttributeSpecs(
  deceased: Partial<Matter> | undefined
): AttributeSpec<UpdateMatterRequest>[] {
  const matter = deceased as Matter | undefined;
  return [
    deceasedNameField(matter, { withDateOfDeathBadge: false }),
    decedentDateField('Date of death', 'decedentDateOfDeath', matter),
    decedentDateField('Date of birth', 'decedentDateOfBirth', matter),
    decedentAddressField(matter)
  ];
}

// =============================================================================
// Matter management
// =============================================================================

/**
 * **The matter's office — a picker over the firm's real offices, or no row at
 * all** (VALE-549).
 *
 * The office decides which of the firm's letterheads this matter's
 * correspondence prints on, so it is a matter-level fact rather than a
 * decoration — but it is one that most firms do not have. Which offices the
 * picker may offer, and when there is nothing to render at all, is
 * {@link resolveMatterOfficeOptions}: the *Manage Estate Details* drawer asks
 * the same question of the same matter, and a second copy of the rule is how
 * the two surfaces come to disagree about one estate's letterhead.
 *
 * Deliberately the same shape as the enquiry drawer's own office row
 * (`components/leads/lead-field-specs.ts`), because it is the same question
 * asked of the record a converted enquiry becomes — and the enquiry's office is
 * what seeds it.
 *
 * Returns an **array** rather than a spec so the empty-firm case is expressible:
 * a hidden row would still occupy a slot in the *See more* count.
 */
function officeFields(
  matter: Matter | undefined
): AttributeSpec<UpdateMatterRequest>[] {
  const { assigned, options: offices } = resolveMatterOfficeOptions(matter);

  // A firm with no offices and a matter naming none: there is nothing to pick
  // from and nothing to show, so the field does not exist.
  if (offices.length === 0) return [];

  return [
    {
      label: 'Office',
      display: assigned?.name ?? null,
      draft: assigned ? String(assigned.id) : NO_OFFICE,
      editor: {
        kind: 'select',
        options: [
          // Keeps the field emptiable: a matter may legitimately be run
          // firm-wide, which is what every matter carried before offices.
          { value: NO_OFFICE, label: 'Firm-wide' },
          ...offices.map((office) => ({
            value: String(office.id),
            label: office.name
          }))
        ]
      },
      /*
        **An office is a reference to a record, not one of a fixed set of
        states**, so the read-out is plain text rather than the badge a `select`
        otherwise derives — the same call {@link actingSolicitorField} makes for
        a person, and the same one the enquiry drawer's office row makes. A pill
        here would also render the identical field pilled for a multi-office
        firm and absent for every other one.
      */
      plainValue: true,
      /*
        Clears to the sentinel rather than the default `''`: `toPatch` reads
        only `NO_OFFICE` as "firm-wide", and `Number('')` is `0` — which would
        post `officeId: 0` and 400 on the backend's `@IsPositive()`.
      */
      clearTo: NO_OFFICE,
      toPatch: (draft) => {
        const next = asText(draft);
        if (next === NO_OFFICE) return { officeId: null };
        const officeId = Number(next);
        if (!Number.isInteger(officeId)) return null;
        return { officeId };
      }
    }
  ];
}

/** What `roles` means to the picker when nobody is allocated. */
const NO_SOLICITOR = '__none__';

/**
 * How "nobody is allocated" reads, on both surfaces that say it.
 *
 * One constant rather than the same two words typed in the enquiry band's
 * picker and again in the estate card's combined row — the card's collapsed
 * read-out falls back to it when a matter has assisting members but no
 * solicitor, and the two must not drift into different vocabulary for one
 * state.
 */
const NO_ONE_YET = 'No one yet';

/**
 * The matter's **acting solicitor**, as a row of the shared field grid.
 *
 * **`roles` is a full sync, not a merge** — `MattersService.update` hands the
 * array straight to `rolesService.syncRoles`, which makes the payload the
 * matter's complete role list. So the patch has to carry every *other* role
 * forward: sending `[{ acting_solicitor }]` alone would silently unassign every
 * assisting member on the matter, from a control that says nothing about them.
 * That is the trap this function exists to close, and it is why the spec needs
 * the whole matter rather than just the current solicitor's id.
 *
 * Rendered by both the matter Overview's enquiry band (`lead-matter-panel.tsx`)
 * and its estate-details card. It is deliberately one function: the trap above
 * is invisible at a call site, so a second copy would be a second chance to
 * ship it.
 */
export function actingSolicitorField(
  matter: Matter | undefined,
  users: LawFirmUser[]
): AttributeSpec<UpdateMatterRequest> {
  const currentId = matter?.actingSolicitor?.id ?? null;
  const current = users.find((user) => user.id === currentId);

  /** Every role except the acting one, which this control replaces. */
  const otherRoles = rolesOtherThan(matter, 'acting_solicitor');

  return {
    label: 'Acting solicitor',
    // The id while the roster resolves, so an allocated matter never reads as
    // unallocated for a frame and invites an overwrite of a value it never
    // showed — the same rule `staffField` follows on the enquiry side.
    display: current
      ? getUserDisplayName(current)
      : currentId != null
        ? `#${currentId}`
        : null,
    draft: currentId != null ? String(currentId) : NO_SOLICITOR,
    editor: {
      kind: 'select',
      options: [
        { value: NO_SOLICITOR, label: NO_ONE_YET },
        ...users.map((user) => ({
          value: String(user.id),
          label: getUserDisplayName(user)
        }))
      ]
    },
    /*
      **A person is a reference, not one of a fixed set of states**, so the
      read-out is plain text rather than the badge a `select` otherwise derives
      — `AttributeSpec.plainValue`'s own doc names exactly this case
      (`Intake owner`, `Preferred solicitor`) and the exact symptom: *"the same
      solicitor a pill in this grid and plain text as `With` in the
      Meeting rail an inch away."*

      Set here rather than on the card, so the enquiry band a few pixels above
      it reads the same way — §2's rule is that a surface changes the shared
      thing for everyone or does not change it, and a person-as-pill was the
      inconsistency `plainValue` exists to end. Caught by the visual gate on the
      card, which is the first surface to render this row beside a genuine
      state badge.
    */
    plainValue: true,
    /*
      A `select` derives the clear `×`, and this one is legitimately clearable —
      "no one yet" is a real state for an allocated matter to return to. It must
      clear to `NO_SOLICITOR` rather than the default `''`, though: `toPatch`
      below reads only that sentinel as "nobody", and `Number('')` is `0`, so an
      empty draft would post `userId: 0` as the acting solicitor.
    */
    clearTo: NO_SOLICITOR,
    toPatch: (draft) => {
      if (typeof draft !== 'string') return null;
      if (draft === NO_SOLICITOR) return { roles: otherRoles };
      const userId = Number(draft);
      if (!Number.isInteger(userId)) return null;
      return {
        roles: [...otherRoles, { userId, role: 'acting_solicitor' as const }]
      };
    }
  };
}

/**
 * **Who is running this matter — the acting solicitor and the assisting team
 * members, as one row** (VALE-166).
 *
 * VALE-130 shipped these as two stacked rows, each a control over half of one
 * answer. That cost two of the card's summary slots to say one thing, and it
 * is why they are now one `principalAndMembers` row with a popover holding both
 * pickers — the same treatment the `Deceased` row's four name parts get.
 *
 * **Folding them together *removes* the trap rather than restating it.** `roles`
 * is handed straight to `rolesService.syncRoles`, which makes the payload the
 * matter's complete role list — so as two rows, each control had to remember to
 * carry the other's assignments forward, and neither the type system nor a
 * one-sided test could catch a version that did not. One draft holding both
 * makes the losing patch unexpressible: `toPatch` below always writes both
 * halves, so assigning or clearing either can never unassign the other. That is
 * the same guarantee the two-row version *asserted*, now structural.
 *
 * **The collapsed row must not hide that assisting members exist.** It reads out
 * the solicitor with the member count appended — `Jane Doe +2` — rather than the
 * solicitor alone, and an unallocated matter that nonetheless has members reads
 * `No one yet +2` rather than falling back to the grid's `Set acting solicitor…`
 * placeholder, which would silently drop them from the summary. Only a matter
 * with neither reads as empty.
 *
 * **It keeps the label `Acting solicitor`.** The row now covers both fields, so
 * a name like *Matter team* is arguably more honest — but the enquiry band a few
 * pixels above renders {@link actingSolicitorField} under that exact label, and
 * two labels for the same allocation on one screen is a worse cost than a label
 * that under-describes its popover. The popover's own headings name both halves.
 *
 * **No `×`.** `principalAndMembers` is deliberately absent from `offersClear`:
 * on a combined row one affordance would have to mean *clear both*, and a
 * coordinator reaching for it to unassign a solicitor would silently drop the
 * whole team. Both halves clear inside the popover — `No one yet`, and
 * unticking — which is the gesture that can say which one it meant.
 */
export function matterTeamField(
  matter: Matter | undefined,
  users: LawFirmUser[]
): AttributeSpec<UpdateMatterRequest> {
  const actingId = matter?.actingSolicitor?.id ?? null;
  const assistingIds = (matter?.roles ?? [])
    .filter((assignment) => assignment.role === 'assisting_member')
    .map((assignment) => assignment.user.id)
    .filter((id): id is number => typeof id === 'number')
    /*
      A matter whose stored roles hold the same person as both is a real state
      the backend accepts, and it would otherwise read as `Jane Doe +1` where
      the `+1` is Jane. The editor prevents *creating* one; this makes an
      existing one read honestly.
    */
    .filter((id) => id !== actingId);

  const acting = users.find((user) => user.id === actingId);
  /*
    The id while the roster resolves, so an allocated matter never reads as
    unallocated for a frame and invites an overwrite of a value it never showed
    — the same rule {@link actingSolicitorField} follows.
  */
  const actingName = acting
    ? getUserDisplayName(acting)
    : actingId != null
      ? `#${actingId}`
      : null;

  /** Every role except the two this control replaces. */
  const otherRoles = rolesOtherThan(matter, [
    'acting_solicitor',
    'assisting_member'
  ]);

  const suffix = assistingIds.length > 0 ? ` +${assistingIds.length}` : '';

  return {
    label: 'Acting solicitor',
    display:
      actingName != null
        ? `${actingName}${suffix}`
        : assistingIds.length > 0
          ? `${NO_ONE_YET}${suffix}`
          : null,
    draft: {
      principal: actingId != null ? String(actingId) : null,
      members: assistingIds.map(String)
    },
    editor: {
      kind: 'principalAndMembers',
      principalLabel: 'Acting solicitor',
      membersLabel: 'Assisting team members',
      /*
        The whole roster, both halves. The editor drops whoever is *currently
        drafted* as the solicitor from the member list — live, which a spec
        built from the committed matter cannot do.
      */
      options: users.map((user) => ({
        value: String(user.id),
        label: getUserDisplayName(user)
      })),
      noneLabel: NO_ONE_YET
    },
    /*
      **A person is a reference, not one of a fixed set of states**, so the
      read-out is plain text rather than a badge — the same call
      {@link actingSolicitorField} makes, kept in agreement so the enquiry band
      and this row read identically. (`principalAndMembers` is not in the
      read-out's `pill` derivation either, so this is belt-and-braces rather
      than load-bearing.)
    */
    plainValue: true,
    toPatch: (draft) => {
      if (typeof draft === 'string') return null;
      const next = draft as AttributePrincipalMembers | null;
      const principal = next?.principal ?? null;
      const roles = [...otherRoles];

      const principalId = principal != null ? Number(principal) : null;
      // `Number('')` is 0, so an id has to be a real integer before it becomes
      // an assignment — the guard `actingSolicitorField`'s own `clearTo` note
      // exists for, restated here because this draft reaches the same hazard by
      // a different route.
      if (
        principalId != null &&
        Number.isInteger(principalId) &&
        principalId > 0
      ) {
        roles.push({ userId: principalId, role: 'acting_solicitor' as const });
      }

      for (const member of next?.members ?? []) {
        const userId = Number(member);
        if (!Number.isInteger(userId) || userId <= 0) continue;
        // Never both, even if a stale draft says so — one person cannot be
        // their own assistant, and `syncRoles` would happily store it.
        if (userId === principalId) continue;
        roles.push({ userId, role: 'assisting_member' as const });
      }

      return { roles };
    }
  };
}

// =============================================================================
// The field list
// =============================================================================

/**
 * **The four rows the card opens on** — the ones a coordinator arriving at a
 * matter is actually looking for.
 *
 * Exported so the card and its tests read one list rather than two that agree
 * until they do not: `SUMMARY_ROWS.length` is what the *See more* control
 * counts against, and the same array is the assertion.
 *
 * **Six became four in VALE-166, and neither row was dropped.** `Assisting team
 * members` folded into {@link matterTeamField} — the same allocation, one row —
 * and `Date of death` moved behind *See more*, reappearing on the `Deceased`
 * row as a badge. So nothing that was readable here stopped being readable; two
 * summary slots were bought back for the Overview's fold budget.
 */
export const MATTER_ESTATE_SUMMARY_ROWS = [
  'Matter reference',
  'Acting solicitor',
  'Deceased',
  'Client'
] as const;

/**
 * The estate record, as one column of four rows with the rest behind *See more*.
 *
 * A pure function of the matter and the firm's roster — no hooks, no side
 * effects — so every rule above is a unit-testable `toPatch` / `validate`
 * rather than a mount-time branch. The card calls it on every render with the
 * latest matter snapshot, so a committed field's new value is what the next
 * render reads.
 *
 * ## Four, then the rest — and the card is a column again
 *
 * VALE-130 put all eight rows on a **full-width three-column band** above the
 * Overview grid, to stop the one-column version pushing `MatterProgressCard`'s
 * CTAs below the fold. It stopped that and cost something else: three columns
 * of label/value pairs read row-major, which interleaved the matter's admin
 * fields with the deceased's, and the one `fullWidth` row spent the whole card
 * width on an address. The band did not read as a record at all.
 *
 * So the card goes back into the left column — **below** Notes and
 * `MatterProgressCard` this time, which is what keeps the CTAs above the fold
 * without the band — and earns its place there by being short: four rows, one
 * column, and a *See more* that reveals the remaining four.
 *
 * ## Why these four
 *
 * `Matter reference` and `Acting solicitor` are what and who, and the sidebar
 * says nothing about either. `Deceased` is what every downstream form needs —
 * and it now carries the date of death as a badge, so that fact stayed in the
 * summary while its row did not. `Client` is the person the firm acts for, and
 * it is the row this card previously did not have at all — see
 * {@link clientField} for why it is read-only.
 *
 * The five behind the control are the ones you look up rather than scan: the
 * three dates (of death, of birth, and of the will), the death certificate
 * number, and the residential address. `Date of death` is there **as the
 * place it is edited**, which is why moving it did not make it unreadable:
 * the badge above is the read.
 *
 * ## `hidden`, not omission
 *
 * The extra rows are always built and gated with `hidden` — the shared grid
 * drops a hidden row from the layout, the `Tab` chain and the accessibility
 * tree, *and* animates it in and out (`components/attributes/CLAUDE.md` §5). An
 * omitted row has no before/after to animate against, so expanding would jump.
 */
export function buildMatterEstateAttributeSpecs(
  matter: Matter | undefined,
  users: LawFirmUser[],
  { expanded = false }: { expanded?: boolean } = {}
): MatterAttributeBlock[] {
  const whenExpanded = <T extends AttributeSpec<UpdateMatterRequest>>(
    field: T
  ): T => ({ ...field, hidden: !expanded });

  return [
    {
      kind: 'rows',
      /*
        `title: null` — no group heading. One column of six rows does not need
        dividing, and VALE-90's four sections spent a full uppercase heading and
        a hairline on a single row each.
      */
      title: null,
      columns: 1,
      fields: [
        matterReferenceField(matter),
        matterTeamField(matter, users),
        deceasedNameField(matter),
        clientField(matter),
        /*
          `Office` leads the revealed rows, ahead of the deceased's own fields:
          it is the other *matter-level* fact on this card, so it belongs beside
          `Matter reference` and `Acting solicitor` rather than among the
          decedent's dates and address.

          Revealed rather than promoted into the four-row summary: the summary
          budget is a deliberate design (see {@link MATTER_ESTATE_SUMMARY_ROWS}),
          and this row does not exist at all for a firm with no offices — so
          spending a permanent summary slot on it would cost every other firm a
          row to show nothing. Spread, not a single entry, for that same reason
          — see {@link officeFields}.
        */
        ...officeFields(matter).map(whenExpanded),
        /*
          `Date of death` leads the deceased's revealed rows rather than trailing
          `Date of birth`: it is the one a coordinator opens the control *for*,
          having just read it as a badge on the `Deceased` row above and wanting
          to correct it.
        */
        whenExpanded(
          decedentDateField('Date of death', 'decedentDateOfDeath', matter)
        ),
        whenExpanded(
          decedentDateField('Date of birth', 'decedentDateOfBirth', matter)
        ),
        /*
          Will date trails the two decedent dates rather than leading — it is
          about the document, not the person, so it sits closer to the death
          certificate number it neighbours (VALE-128).
        */
        whenExpanded(decedentDateField('Will date', 'willDate', matter)),
        whenExpanded(deathCertificateField(matter)),
        /*
          The will's whereabouts, in the will's own group (VALE-1402), but
          **below** the date/certificate-number pair rather than between them.
          It is `fullWidth`, so inserting it directly under `Will date` would
          leave that row's right half empty and push the certificate number onto
          a line of its own: one new row costing two gaps. Below the pair it
          owns a clean line and every existing pairing on the card is untouched.

          Behind `whenExpanded` like its neighbours: it answers "where do we get
          the original from", which matters once and then never again.
        */
        whenExpanded(willStorageLocationField(matter)),
        /*
          Occupation and place of death sit after the certificate number and
          before the address, which is the order a court application recites
          them — and it keeps place of death beside the address it is most
          easily confused with, where the two labels can be read against each
          other (VALE-809).
        */
        whenExpanded(
          decedentTextField('Occupation', 'decedentOccupation', matter)
        ),
        whenExpanded(
          decedentTextField('Place of death', 'decedentPlaceOfDeath', matter)
        ),
        whenExpanded(decedentAddressField(matter))
      ]
    }
  ];
}

// =============================================================================
// Estate bank account
// =============================================================================

/**
 * The account the estate's own money is collected into, as the request stores
 * it — three strings, all present together or not at all.
 *
 * It was *"as the drawer holds it mid-edit"* while three rows assembled the
 * account one part at a time and the drawer held an overlay of unsent parts
 * (VALE-892). One popover fills every part before committing, so there is no
 * mid-edit state outside the editor and this is now simply the shape the patch
 * carries (see {@link estateBankAccountField}).
 */
export interface EstateBankAccountParts {
  accountName: string;
  bsb: string;
  accountNumber: string;
}

/**
 * How each part is named in a refusal.
 *
 * A registry rather than three inline strings, because the message reads
 * `<part> was not saved. <schema's reason>` and the part name is the only thing
 * that tells the reader which of the three to go and fix.
 */
const ACCOUNT_PART_LABELS: Record<keyof EstateBankAccountParts, string> = {
  accountName: 'Account name',
  bsb: 'BSB',
  accountNumber: 'Account number'
};

/**
 * The order the parts are checked in, which is the order they appear in the
 * popover — so the refusal names the topmost unfinished part rather than
 * whichever key the object happened to enumerate first.
 */
const ACCOUNT_PART_LABELS_ORDER = [
  'accountName',
  'bsb',
  'accountNumber'
] as const satisfies readonly (keyof EstateBankAccountParts)[];

/**
 * The request's own rule for the account, unwrapped once so the rows can ask it
 * about a single part.
 *
 * Bound rather than restated: the lengths and the digits-only rules live on
 * `UpdateMatterRequestSchema` and are what `updateMatterAction` actually
 * enforces, so a second copy here would be correct only until somebody moves
 * one — and its refusal messages are the schema's own, which is what makes them
 * agree with the 400 the same payload would earn.
 */
const ESTATE_BANK_ACCOUNT_SHAPE =
  UpdateMatterRequestSchema.shape.estateBankAccount.unwrap().shape;

/**
 * **The estate bank account — one row over one indivisible request field**
 * (VALE-892, folded into one field by VALE-1211).
 *
 * ## What VALE-892 solved, and why almost none of it is left
 *
 * `estateBankAccount` is `.optional()` on `UpdateMatterRequestSchema` but not
 * partial: whenever the key is present, all three parts must be there and valid
 * (a 6-digit BSB, a 6–10 digit account number, an account name of at least four
 * characters). `updateMatterAction` `safeParse`s every patch **before any HTTP
 * call**, so a row sending its own part alone would fail locally on a matter
 * with no account yet — the first field typed could never commit, which would
 * make adding an account impossible rather than merely awkward.
 *
 * Three rows over that shape needed a machine to survive it: each row asked
 * whether its **siblings** were already valid, and either sent the whole triple
 * or `localCommit`ed into a host-held overlay that the completing row then swept
 * up. `OTHER_ACCOUNT_PARTS`, `accountPartIsValid`, the `sendable` branch and the
 * drawer's `unsentAccount` state were all that machine, and all of it is gone.
 *
 * **One popover removes the problem rather than solving it again.** The reader
 * fills every part in one editor and the commit fires once, on dismiss, always
 * carrying all three — so there is no such thing as a part arriving alone, and
 * nothing to buffer. The account can no longer be sent in halves because there
 * is no gesture that sends half of it.
 *
 * ## What `validate` refuses, and why refusing beats buffering here
 *
 * An incomplete account is refused, naming the missing part. That replaces the
 * overlay, and it is strictly safer than what it replaces: a refused commit
 * **keeps the reader's draft in the open editor** (`use-attribute-commit.ts` —
 * the cell wears the refusal ring and the value stays put), whereas the overlay
 * was in-memory drawer state that vanished silently when the drawer closed. The
 * partial work is more durable now, not less.
 *
 * A malformed part is refused with the schema's own words, exactly as before.
 */
export function estateBankAccountField(
  matter: Matter | undefined
): AttributeSpec<UpdateMatterRequest> {
  const stored = matter?.estateBankAccount;
  const account: AttributeBankAccount | null = stored
    ? {
        accountName: stored.accountName ?? '',
        bsb: stored.bsb ?? '',
        accountNumber: stored.accountNumber ?? ''
      }
    : null;

  // Digits are what the column stores; any grouping a reader types is display.
  const normalise = (
    key: keyof EstateBankAccountParts,
    value: string | null | undefined
  ): string =>
    key === 'accountName'
      ? (value ?? '').trim()
      : (value ?? '').replace(/\D/g, '');

  const parts = (draft: AttributeDraft): EstateBankAccountParts | null => {
    if (draft === null || typeof draft !== 'object') return null;
    const next = draft as AttributeBankAccount;
    return {
      accountName: normalise('accountName', next.accountName),
      bsb: normalise('bsb', next.bsb),
      accountNumber: normalise('accountNumber', next.accountNumber)
    };
  };

  return {
    // `Account`, not `Estate bank account` — `ESTATE_BANK_ACCOUNT_GROUP_TITLE`
    // sits directly above and already says it. See the contacts row's own note.
    label: 'Account',
    // Owns its line, for the reason `contact-attribute-specs.ts`'s row records:
    // at two columns the two numbers truncate with the other half empty.
    fullWidth: true,
    display: formatBankAccountSummary(account) || null,
    draft: account,
    // The two numbers, without the read-out's `·`.
    copyText: bankAccountCopyText(account),
    editor: { kind: 'bankAccount', accountName: true },
    /*
      No `×` once an account is on the record (VALE-1505). `UpdateMatterRequest`'s
      `estateBankAccount` is optional and not nullable, so there is no request
      that means "remove the account": `toPatch` returned `null` for an emptied
      draft, and since LF-6250 a `null` patch is a VISIBLE refusal, the one
      gesture the `×` exists for produced *"Account was not saved. That value
      cannot be saved."* on this row (VALE-307's shape, on a kind that did not
      exist when VALE-307 was fixed). `required` withholds the affordance, and
      `validate` below says why an emptied popover is refused rather than
      leaving it to the generic copy.
    */
    required: stored != null,
    validate: (value) => {
      const next = parts(value);
      const emptied =
        !next || (!next.accountName && !next.bsb && !next.accountNumber);
      /*
        Nothing captured at all is a legitimate state — a matter need not have an
        estate account — so an empty popover dismissed unchanged is not a
        refusal. Emptying an account the record already holds is different: the
        request cannot carry it (see `required` above), so the reader is told
        rather than shown a wordless refusal.
      */
      if (emptied) {
        return stored
          ? 'Account was not saved. An estate account cannot be removed here; edit its details instead.'
          : null;
      }

      /*
        Bound to the request's own schema rather than restated: the lengths and
        the digits-only rules live on `UpdateMatterRequestSchema` and are what
        `updateMatterAction` actually enforces, and its refusal messages are what
        make this agree with the 400 the same payload would earn.
      */
      for (const key of ACCOUNT_PART_LABELS_ORDER) {
        const parsed = ESTATE_BANK_ACCOUNT_SHAPE[key].safeParse(next![key]);
        if (!parsed.success) {
          return `${ACCOUNT_PART_LABELS[key]} was not saved. ${
            parsed.error.issues[0]?.message ?? 'Check the value and try again.'
          }`;
        }
      }
      return null;
    },
    toPatch: (value) => {
      const next = parts(value);
      if (!next) return null;
      /*
        A cleared account sends nothing rather than an empty triple: the schema
        refuses a partial object, and `estateBankAccount` being optional means
        *absent* is how "no account" is expressed. `validate` has already let
        this through as the legitimate empty state.
      */
      if (!next.accountName && !next.bsb && !next.accountNumber) return null;
      return { estateBankAccount: next };
    }
  };
}

// =============================================================================
// The Manage Estate Details drawer's field list
// =============================================================================

/**
 * The drawer's section headings.
 *
 * Exported because the host matches on them to splice the identity-variant rows
 * and the two kept components into the right sections — the same reason
 * `contact-attribute-specs.ts` exports its group titles rather than letting
 * `contact-attribute-grid.tsx` restate them.
 */
export const MATTER_GROUP_TITLE = 'Matter';
export const DECEASED_GROUP_TITLE = 'Deceased';
export const ESTATE_BANK_ACCOUNT_GROUP_TITLE = 'Estate bank account';
export const MATTER_MANAGEMENT_GROUP_TITLE = 'Matter management';

/**
 * **The full estate record the Manage Estate Details drawer manages** (VALE-892).
 *
 * The card's {@link buildMatterEstateAttributeSpecs} is a *subset* of this list
 * — four rows and a *See more* — and both are built from the same field
 * builders above, which is the point: a rule about how a decedent date clears,
 * or how `roles` full-syncs, is written once and both surfaces inherit it. The
 * two differ only in what they show and how they group it, which is a surface
 * decision.
 *
 * ## The grouping
 *
 * Four sections, two columns each — the treatment's own density, and the same
 * the Contacts drawer takes:
 *
 * - **Matter** — the firm's reference. The demo-matter control continues this
 *   section as a kept component (the host appends it), because the checkbox
 *   carries a paragraph of consequence that no label/value row can hold.
 * - **Deceased** — the person, laid out as the identity 2×2 the Contact form
 *   established: `Deceased` above `Other names`, `Residential address` above
 *   `Other addresses`, then the dates and the recital fields in the order a
 *   court application asks for them.
 * - **Estate bank account** — see {@link estateBankAccountFields}.
 * - **Matter management** — who runs it, and out of which office.
 *
 * ## `startsRow`, on every left-hand row
 *
 * The grid flows fields into columns in array order, so a pairing only holds if
 * its left-hand row is anchored: `Office` disappears entirely for a firm with no
 * offices, and every row after it would otherwise change column
 * (`components/attributes/CLAUDE.md` — the same trap the Contact identity block
 * documents).
 *
 * A pure function of the matter, the roster and the account draft — no hooks —
 * so every rule stays a unit-testable `toPatch` / `validate`.
 */
export function buildMatterDrawerAttributeBlocks(
  matter: Matter | undefined,
  users: LawFirmUser[]
): MatterAttributeBlock[] {
  const anchor = <T extends AttributeSpec<UpdateMatterRequest>>(
    field: T
  ): T => ({
    ...field,
    startsRow: true
  });

  return [
    {
      kind: 'rows',
      title: MATTER_GROUP_TITLE,
      fields: [anchor(matterReferenceField(matter))]
    },
    {
      kind: 'rows',
      title: DECEASED_GROUP_TITLE,
      fields: [
        /*
          The host splices `Other names` and `Other addresses` in after these
          two, which is what makes the 2×2. Both anchors are declared here so the
          block reads correctly even on a host that does not splice.
        */
        anchor(deceasedNameField(matter, { withDateOfDeathBadge: false })),
        decedentAddressField(matter),
        anchor(
          decedentDateField('Date of death', 'decedentDateOfDeath', matter)
        ),
        decedentDateField('Date of birth', 'decedentDateOfBirth', matter),
        /*
          Will date leads the second pair rather than trailing the two decedent
          dates: it is about the document, so it sits beside the death
          certificate number (VALE-128).
        */
        anchor(decedentDateField('Will date', 'willDate', matter)),
        deathCertificateField(matter),
        // Below the will-date / certificate-number pair, not between them. See
        // the Overview card above for why a `fullWidth` row cannot sit inside a
        // pair without emptying half of it.
        anchor(willStorageLocationField(matter)),
        /*
          Occupation and place of death in the order a court application recites
          them, and place of death paired with occupation rather than with the
          address it is most easily confused with (VALE-809).
        */
        anchor(decedentTextField('Occupation', 'decedentOccupation', matter)),
        decedentTextField('Place of death', 'decedentPlaceOfDeath', matter)
      ]
    },
    {
      kind: 'rows',
      title: ESTATE_BANK_ACCOUNT_GROUP_TITLE,
      /*
        **One row** (VALE-1211). It was three: the name owning its own line —
        the longest value and the only prose one — with the two identifiers
        paired beneath it, where a reader checks them against a bank statement
        together.

        That pairing is now inside the popover, and the row itself reads out the
        two identifiers the reader is checking. The name is not shown at rest: it
        is typed once from the top of a statement and never re-read, and on this
        drawer it was spending a whole `fullWidth` line to say so.
      */
      fields: [anchor(estateBankAccountField(matter))]
    },
    {
      kind: 'rows',
      title: MATTER_MANAGEMENT_GROUP_TITLE,
      fields: [
        /*
          The office decides which letterhead every letter on this estate prints
          on, so it leads the section rather than trailing the people on it. It
          is absent entirely for a firm with no offices, which is why the row
          after it is anchored rather than trusting the parity.
        */
        ...officeFields(matter).map(anchor),
        /*
          Deliberately NOT anchored, so it pairs with `Office` on one line.
          Anchoring it as well put each on its own row and left the whole right
          half of the section empty. It still opens the row correctly for a firm
          with no offices, because then it is simply first in the block.
        */
        matterTeamField(matter, users)
      ]
    }
  ];
}
